import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppConfig, RuntimeConfigPayload } from '../../models/app-config';

/**
 * Thrown when runtime config is read before the sync preload / initialize()
 * has populated it. Failing loud here prevents requests being built against
 * `undefined/...` URLs (which would otherwise go out same-origin with a
 * bearer token attached).
 */
export class ConfigNotLoadedError extends Error {
  constructor() {
    super(
      'Runtime config accessed before it was loaded — config.json preload/initialize did not complete',
    );
    this.name = 'ConfigNotLoadedError';
  }
}

@Injectable({
  providedIn: 'root',
})
export class ConfigService {
  private http = inject(HttpClient);
  private appConfig?: AppConfig;
  private initPromise: Promise<void> | null = null;
  private isInitialized = false;

  constructor() {
    this.preloadConfigSync();
  }

  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    if (!this.initPromise) {
      this.initPromise = this.fetchRuntimeConfig().then((config) => {
        const loadedConfig = config?.adminPortalConfig ?? config ?? {};
        this.appConfig = {
          ...this.appConfig,
          ...loadedConfig,
        } as AppConfig;
        this.isInitialized = true;
      });
    }

    return this.initPromise;
  }

  private async fetchRuntimeConfig(): Promise<RuntimeConfigPayload> {
    const base = await firstValueFrom(
      this.http.get<RuntimeConfigPayload>('/config.json', { responseType: 'json' }),
    );
    let overlay: RuntimeConfigPayload | null = null;
    try {
      overlay = await firstValueFrom(
        this.http.get<RuntimeConfigPayload>('/config.local.json', { responseType: 'json' }),
      );
    } catch {
      // config.local.json is optional
    }
    if (overlay) {
      const baseConfig = base?.adminPortalConfig ?? base ?? {};
      const overlayConfig = overlay?.adminPortalConfig ?? overlay ?? {};
      return { adminPortalConfig: { ...baseConfig, ...overlayConfig } };
    }
    return base;
  }

  // Backward-compatible alias used in tests and older call sites.
  public async loadConfig(): Promise<void> {
    return this.initialize();
  }

  public get config(): AppConfig {
    if (!this.appConfig) {
      throw new ConfigNotLoadedError();
    }
    return this.appConfig;
  }

  private preloadConfigSync(): void {
    // MSAL-factories run before APP_INITIALIZER / provideAppInitializer
    // so we need to preload the config synchronously from there
    const loaded = this.trySyncLoad('/config.json');
    if (loaded) {
      const loadedConfig = loaded?.adminPortalConfig ?? loaded ?? {};
      this.appConfig = {
        ...this.appConfig,
        ...loadedConfig,
      } as AppConfig;
      this.isInitialized = true;
    }

    // Optional local bypass overlay — gitignored, never deployed
    const localOverlay = this.trySyncLoad('/config.local.json');
    if (localOverlay) {
      const overlayConfig = localOverlay?.adminPortalConfig ?? localOverlay ?? {};
      this.appConfig = { ...this.appConfig, ...overlayConfig } as AppConfig;
    }
  }

  private trySyncLoad(url: string): RuntimeConfigPayload | null {
    if (typeof XMLHttpRequest === 'undefined') {
      return null;
    }

    try {
      const request = new XMLHttpRequest();
      request.open('GET', url, false);
      request.send();

      if (request.status >= 200 && request.status < 300 && request.responseText) {
        return JSON.parse(request.responseText) as RuntimeConfigPayload;
      }
    } catch {
      return null;
    }
    return null;
  }
}
